import { alertsAtom } from '@/atoms';
import { db } from '@/data';
import { useNotifications, useUserInfo } from '@/hooks';
import { startCase } from '@/utils';
import {
  apiRootUrl,
  authenticationRootUrl,
  authenticationScheme,
  showAdGroups,
  showClearCacheButton,
  tenantId,
} from '@/utils/config';
import {
  AccountCircle as AccountCircleIcon,
  Whatshot as WhatshotIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useAtom } from 'jotai';
import ky from 'ky';
import { Fragment, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ColorSchemeButton } from './ColorSchemeButton';
import { NotificationList } from './NotificationList';
import { useAccelerometerAlertStream } from './useAccelerometerAlertStream';
import { useNotificationStream } from './useNotificationStream';

export function LoggedInAvatar({ auth }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const { data: userInfo } = useUserInfo();
  const open = Boolean(anchorEl);
  const id = open ? 'login-popover' : undefined;
  const { data: notifications } = useNotifications();
  useNotificationStream();
  useAccelerometerAlertStream();
  const [alerts] = useAtom(alertsAtom);
  const queryClient = useQueryClient();

  function handleAvatarClick(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleProfileClick() {
    navigate(`/resources/people/${userInfo.person.code}`);
    handleClose();
  }

  function handleLogoutClick() {
    ky.delete(`${apiRootUrl}/${authenticationScheme}/logout`, {
      headers: { authorization: `Bearer ${auth.user?.access_token}` },
    })
      .then(() => auth.removeUser())
      .then(() => db.delete())
      .finally(() => {
        queryClient.clear();

        handleClose();

        window.location.href = `${authenticationRootUrl}/${tenantId}/${authenticationScheme}/logout`;
      });
  }

  function handleClearCacheClick() {
    queryClient.clear();
  }

  return (
    <Fragment>
      <IconButton
        sx={{ p: 0 }}
        aria-describedby={id}
        onClick={handleAvatarClick}
        id="login"
        size="large"
      >
        <Badge
          badgeContent={notifications?.length + alerts?.length || 0}
          color="error"
          overlap="circular"
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: 16,
              bgcolor: 'primary.dark',
            }}
            src={userInfo?.person?.picture}
          >
            {[userInfo?.forenames?.[0], userInfo?.surname?.[0]]
              .filter(Boolean)
              .join('') ||
              [userInfo?.person?.forenames?.[0], userInfo?.person?.surname?.[0]]
                .filter(Boolean)
                .join('') || <AccountCircleIcon color="secondary" />}
          </Avatar>
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="caption">
            Logged in as <b>{userInfo?.displayName ?? userInfo?.username}</b>
          </Typography>
        </Box>
        {userInfo?.person?.code && (
          <Fragment>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">{`${userInfo.person.forenames} ${userInfo.person.surname}`}</Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  {userInfo.person.collarNumber}
                </Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        Rank
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {(userInfo.person.rank || {}).name}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="caption" color="textSecondary">
                        Role
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {userInfo.person.role}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {Object.entries(userInfo.person.groups || {}).map(
                    ([type, values], index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {startCase(type)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {(values || []).join(', ')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </Box>
          </Fragment>
        )}
        {alerts?.length > 0 && (
          <Fragment>
            <Divider />
            <Box sx={{ padding: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  sx={{ pb: 1 }}
                  variant="subtitle2"
                  color="textSecondary"
                >
                  Accelerometer Alerts
                </Typography>
                <Chip size="small" color="error" label={alerts.length} />
              </Box>
              <List dense disablePadding sx={{ maxWidth: 400 }}>
                {alerts
                  .sort((a, b) => new Date(b.time) - new Date(a.time))
                  .map((alert) => (
                    <ListItemButton
                      dense
                      key={alert.identifier}
                      component={Link}
                      to={`/live/events/${alert.identifier}`}
                    >
                      <ListItemText
                        primary={`${
                          alert.vehicle.driver
                            ? `${alert.driver.forenames} ${alert.driver.surname} (${alert.driver.collarNumber})`
                            : 'Unknown driver'
                        } - ${alert.vehicle.registrationNumber} (${
                          alert.vehicle.fleetNumber
                        })`}
                        secondary={format(
                          new Date(alert.time),
                          'dd/MM/yyyy HH:mm:ss',
                        )}
                      />
                    </ListItemButton>
                  ))}
              </List>
            </Box>
          </Fragment>
        )}
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography sx={{ pb: 1 }} variant="subtitle2" color="textSecondary">
            Authorisation Groups
          </Typography>
          {userInfo?.authorisation?.names?.map((name) => (
            <Box key={name}>
              <Typography variant="caption" color="textSecondary">
                {name}
              </Typography>
            </Box>
          ))}
        </Box>
        {showAdGroups && (
          <Fragment>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography
                sx={{ pb: 1 }}
                variant="subtitle2"
                color="textSecondary"
              >
                Active Directory Groups
              </Typography>
              {userInfo?.groups?.map((identifier) => (
                <Box key={identifier}>
                  <Typography variant="caption" color="textSecondary">
                    {identifier}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Fragment>
        )}
        {notifications?.length > 0 && (
          <Fragment>
            <Divider />
            <Box sx={{ padding: 2 }}>
              <NotificationList notifications={notifications} />
            </Box>
          </Fragment>
        )}
        <Stack
          spacing={1}
          direction="row"
          sx={{ justifyContent: 'flex-end', p: 1 }}
        >
          {showClearCacheButton && (
            <Tooltip title="Clear cache">
              <IconButton
                size="small"
                color="warning"
                onClick={handleClearCacheClick}
              >
                <WhatshotIcon />
              </IconButton>
            </Tooltip>
          )}
          <ColorSchemeButton />
          <Box sx={{ flexGrow: 1 }} />
          {userInfo?.person?.code && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleProfileClick}
            >
              Profile
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={handleLogoutClick}>
            Logout
          </Button>
        </Stack>
      </Popover>
    </Fragment>
  );
}
